import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  Avatar, 
  Button, 
  TextField, 
  FormControlLabel, 
  Checkbox, 
  Link, 
  Grid, 
  Box, 
  Typography, 
  Container, 
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { auth } from '../firebase/firebase';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';

const Login = () => {
  // 表单状态
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // UI状态
  const [isProcessing, setIsProcessing] = useState(false);
  const [localError, setLocalError] = useState('');
  const [loginTab, setLoginTab] = useState(0); // 0: 邮箱, 1: 手机号
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  
  // 手机验证相关
  const recaptchaContainerRef = useRef(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const db = getFirestore();
  
  const { 
    currentUser, 
    login,
    resetPassword, 
    error: authError, 
    clearError,
    loading: authLoading,
    setUserData,
    setIsAdmin,
    googleLogin,
    loginWithApple,
    initPhoneAuth,
    sendPhoneVerificationCode,
    loginWithPhone
  } = useAuth();

  const loading = authLoading || isProcessing;
  
  useEffect(() => {
    const checkUserAndRedirect = async () => {
      if (currentUser) {
        console.log("用户已登录，检查用户角色...");
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log("用户数据:", userData);
            if (userData.role === 'admin') {
              console.log("管理员用户，重定向到管理后台...");
              navigate('/admin');
              return;
            }
          }
          console.log("普通用户，重定向到首页...");
          const { from } = location.state || { from: { pathname: '/' } };
          navigate(from, { replace: true });
        } catch (err) {
          console.error("检查用户角色时出错:", err);
          setLocalError("验证用户权限时出错，请重试");
        }
      }
    };
    
    checkUserAndRedirect();
  }, [currentUser, navigate, location, db]);
  
  const error = localError || authError;

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearError();
    setLocalError('');
    setIsProcessing(true);
    
    try {
      console.log("开始登录流程...");
      
      if (!email || !password) {
        console.log("验证失败：邮箱或密码为空");
        setLocalError('请填写邮箱和密码');
        setIsProcessing(false);
        return;
      }
      
      console.log("表单验证通过，尝试登录...");
      const user = await login(email, password, rememberMe);
      console.log("Firebase登录成功，用户ID:", user.uid);
      
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        console.error("用户文档不存在");
        setLocalError("用户数据不存在，请联系管理员");
        setIsProcessing(false);
        return;
      }
      
      const userData = userDoc.data();
      console.log("获取到用户数据:", userData);
      
      if (userData.role === 'admin') {
        console.log("管理员登录成功，准备重定向...");
        navigate('/admin');
      } else {
        console.log("普通用户登录成功，重定向到首页...");
        const { from } = location.state || { from: { pathname: '/' } };
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("登录失败:", error.code, error.message);
      let errorMessage = "登录失败，请检查您的邮箱和密码";
      
      switch(error.code) {
        case 'auth/invalid-email':
          errorMessage = "邮箱格式不正确";
          break;
        case 'auth/user-not-found':
          errorMessage = "该邮箱未注册";
          break;
        case 'auth/wrong-password':
          errorMessage = "密码错误";
          break;
        case 'auth/too-many-requests':
          errorMessage = "登录尝试次数过多，请稍后再试";
          break;
        case 'auth/network-request-failed':
          errorMessage = "网络连接错误，请检查您的网络";
          break;
        case 'auth/web-storage-unsupported':
          errorMessage = "浏览器存储功能被禁用，请启用Cookie或使用其他浏览器";
          break;
        default:
          errorMessage = `登录失败: ${error.message}`;
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // 检测是否是iOS Safari
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    return ua.includes('iPhone') && ua.includes('Safari') && !ua.includes('Chrome');
  };

  // 处理Google登录
  const handleGoogleLogin = async () => {
    try {
      clearError();
      setLocalError('');
      setIsProcessing(true);
      console.log("开始Google登录流程...");
      
      // 检测设备和浏览器
      const isSafariIOS = isIOSSafari();
      if (isSafariIOS) {
        console.log("检测到iOS Safari，使用安全登录方式");
      }

      const result = await googleLogin();
      const user = result.user;
      console.log("Google登录成功，用户ID:", user.uid);

      // 检查或创建用户文档
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      let userData;
      if (!userDoc.exists()) {
        console.log("创建新用户文档...");
        userData = {
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          createdAt: new Date(),
          updatedAt: new Date(),
          lastLogin: new Date(),
          role: user.email === 'admin@destinycrystal.com' ? 'admin' : 'user',
          favoriteProducts: [],
          cart: []
        };
        await setDoc(userDocRef, userData);
      } else {
        userData = userDoc.data();
        // 更新最后登录时间
        await setDoc(userDocRef, { lastLogin: new Date() }, { merge: true });
        console.log("用户文档已存在，已更新登录时间");
      }

      // 更新上下文中的用户数据
      setUserData(userData);
      setIsAdmin(userData.role === 'admin');

      if (userData.role === 'admin') {
        console.log("管理员登录，重定向到管理后台...");
        navigate('/admin');
      } else {
        console.log("普通用户登录，重定向到首页...");
        const { from } = location.state || { from: { pathname: '/' } };
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Google登录失败:', error.code, error.message);
      console.log('详细错误信息:', JSON.stringify(error, null, 2));
      
      let errorMessage = "Google登录失败";
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = "登录窗口被关闭";
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = "登录窗口被浏览器阻止，请允许弹窗";
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = "登录请求已取消";
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = "网络连接错误，请检查网络";
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = "当前域名未授权使用Google登录，请联系管理员";
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = "Google登录未在Firebase控制台启用";
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = "此邮箱已与其他登录方式关联";
      } else if (isIOSSafari() && error.message && error.message.includes('redirect')) {
        errorMessage = "Safari登录遇到问题，请尝试使用邮箱密码登录";
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 处理Apple登录
  const handleAppleLogin = async () => {
    try {
      clearError();
      setLocalError('');
      setIsProcessing(true);
      console.log("开始Apple登录流程...");
      
      const user = await loginWithApple();
      console.log("Apple登录成功，用户ID:", user.uid);
      
      // 处理登录后跳转
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        console.error("用户文档不存在");
        setLocalError("用户数据不存在，请联系管理员");
        setIsProcessing(false);
        return;
      }
      
      const userData = userDoc.data();
      if (userData.role === 'admin') {
        console.log("管理员登录成功，准备重定向...");
        navigate('/admin');
      } else {
        console.log("普通用户登录成功，重定向到首页...");
        const { from } = location.state || { from: { pathname: '/' } };
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Apple登录失败:', error);
      let errorMessage = "Apple登录失败";
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = "登录窗口被关闭";
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = "登录窗口被浏览器阻止，请允许弹窗";
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = "登录请求已取消";
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = "网络连接错误，请检查网络";
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = "当前域名未授权使用Apple登录，请联系管理员";
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = "Apple登录未在Firebase控制台启用";
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 打开手机登录对话框
  const handleOpenPhoneDialog = () => {
    clearError();
    setLocalError('');
    setPhoneNumber('');
    setVerificationCode('');
    setCodeSent(false);
    setPhoneDialogOpen(true);
  };
  
  // 关闭手机登录对话框
  const handleClosePhoneDialog = () => {
    setPhoneDialogOpen(false);
  };
  
  // 发送手机验证码
  const handleSendVerificationCode = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setLocalError('请输入有效的手机号码');
      return;
    }
    
    try {
      clearError();
      setLocalError('');
      setIsProcessing(true);
      
      // 初始化reCAPTCHA
      const recaptchaVerifier = initPhoneAuth('recaptcha-container');
      
      // 格式化手机号
      const formattedPhoneNumber = phoneNumber.startsWith('+') 
        ? phoneNumber 
        : `+65${phoneNumber}`; // 默认新加坡区号
      
      // 发送验证码
      const confirmation = await sendPhoneVerificationCode(formattedPhoneNumber, recaptchaVerifier);
      setConfirmationResult(confirmation);
      setCodeSent(true);
      
    } catch (error) {
      console.error('发送验证码失败:', error);
      let errorMessage = "发送验证码失败";
      
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = "无效的手机号格式";
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = "短信验证码已达到发送上限";
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 验证码登录
  const handlePhoneLogin = async () => {
    if (!verificationCode) {
      setLocalError('请输入验证码');
      return;
    }
    
    try {
      clearError();
      setLocalError('');
      setIsProcessing(true);
      
      const user = await loginWithPhone(confirmationResult, verificationCode);
      console.log("手机登录成功，用户ID:", user.uid);
      
      // 关闭对话框
      setPhoneDialogOpen(false);
      
      // 处理登录后跳转
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.data();
      
      if (userData.role === 'admin') {
        console.log("管理员登录成功，准备重定向...");
        navigate('/admin');
      } else {
        console.log("普通用户登录成功，重定向到首页...");
        const { from } = location.state || { from: { pathname: '/' } };
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('手机验证登录失败:', error);
      let errorMessage = "验证失败";
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = "验证码错误";
      } else if (error.code === 'auth/code-expired') {
        errorMessage = "验证码已过期";
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setLocalError('请输入邮箱地址');
      return;
    }
    
    try {
      clearError();
      setLocalError('');
      setIsProcessing(true);
      await resetPassword(email);
      setResetSent(true);
    } catch (error) {
      console.error('密码重置失败:', error.code, error.message);
      let errorMessage = '密码重置失败，请稍后重试';
      
      if (error.code === 'auth/invalid-email') {
        errorMessage = "邮箱格式不正确";
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = "该邮箱未注册";
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleRememberMeChange = (event) => {
    setRememberMe(event.target.checked);
  };

  // 手机验证码对话框
  const phoneLoginDialog = (
    <Dialog 
      open={phoneDialogOpen} 
      onClose={handleClosePhoneDialog}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        手机号登录
        <IconButton
          aria-label="close"
          onClick={handleClosePhoneDialog}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {localError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        )}
        
        {!codeSent ? (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              请输入您的手机号，我们将发送验证码短信。
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="phoneNumber"
              label="手机号码"
              type="tel"
              fullWidth
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+65 xxxx xxxx"
              disabled={loading}
            />
            {/* 手机验证的reCAPTCHA容器 */}
            <Box id="recaptcha-container" ref={recaptchaContainerRef} sx={{ mt: 2 }}></Box>
          </>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              验证码已发送至 {phoneNumber}，请输入短信中的6位验证码。
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="verificationCode"
              label="验证码"
              type="text"
              fullWidth
              variant="outlined"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              disabled={loading}
            />
          </>
        )}
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!codeSent ? (
          <Button 
            onClick={handleSendVerificationCode} 
            color="primary"
            variant="contained"
            fullWidth
            disabled={loading || !phoneNumber}
          >
            发送验证码
          </Button>
        ) : (
          <Button 
            onClick={handlePhoneLogin} 
            color="primary"
            variant="contained"
            fullWidth
            disabled={loading || !verificationCode}
          >
            登录
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            登录
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {resetSent && (
            <Alert severity="success" sx={{ width: '100%', mt: 2 }}>
              密码重置邮件已发送，请查收
            </Alert>
          )}
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="邮箱地址"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              error={!!error && error.includes('邮箱')}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="密码"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              error={!!error && error.includes('密码')}
            />
            <FormControlLabel
              control={
                <Checkbox 
                  value="remember" 
                  color="primary" 
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  disabled={loading}
                />
              }
              label="记住我"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? '登录中...' : '邮箱密码登录'}
            </Button>
            
            <Grid container>
              <Grid item xs>
                <Link 
                  component="button"
                  variant="body2" 
                  onClick={handleForgotPassword}
                  disabled={loading}
                >
                  忘记密码？
                </Link>
              </Grid>
              <Grid item>
                <Link 
                  component={RouterLink} 
                  to="/register" 
                  variant="body2"
                >
                  {"没有账号？立即注册"}
                </Link>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }}>其他登录方式</Divider>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoogleLogin}
                  startIcon={<GoogleIcon />}
                  disabled={loading}
                >
                  Google登录
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenPhoneDialog}
                  startIcon={<PhoneIcon />}
                  disabled={loading}
                >
                  手机号登录
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
      
      {/* 手机登录对话框 */}
      {phoneLoginDialog}
    </Container>
  );
};

export default Login;